import { Condition } from '../../condition';
import { IAttribute } from '../../dom';
import { ThEachConditionsBuilder } from './theach-conditions-builder';

/**
 * Represents the parts of a "th:each" attribute value.<br />
 * Example1: candidates_item: ${invenias.assignmentsListItem.candidates}<br />
 * Example2: education_item: ${#lists.sort(invenias.peopleListItem.education, 'startDate', -1)}
 * Example3: positions_item: ${#lists.limit(#lists.sort(candidates_item.positions, 'displayStartDate', -1), 5)}
 * Example4: positions_item: ${#lists.limit(candidates_item.positions, 3)}
 */
export class ThEachAttribute implements IAttribute {
  public static QUALIFIED_NAME = 'th:each';
  name: string;
  srcFieldPath: string;
  conditions: Condition[] | undefined;
  sort?: { fieldPath: string; direction: number };
  limit?: number;

  constructor(
    name: string,
    srcFieldPath: string,
    conditions?: Condition[],
    sort?: { fieldPath: string; direction: number },
    limit?: number
  ) {
    this.name = name;
    this.srcFieldPath = srcFieldPath;
    this.conditions = conditions;
    this.sort = sort;
    this.limit = limit;
  }

  get qualifiedName(): string {
    return ThEachAttribute.QUALIFIED_NAME;
  }

  get value(): string {
    let term: string = this.buildSrcList();

    if (this.sort) {
      term = `#lists.sort(${term}, '${this.sort.fieldPath}', ${this.sort.direction})`;
    }

    if (this.limit) {
      term = `#lists.limit(${term}, ${this.limit})`;
    }

    return `${this.name}: \${${term}}`;
  }

  toString(): string {
    return `${ThEachAttribute.QUALIFIED_NAME}="${this.value}"`;
  }

  private buildSrcList(): string {
    const escapedSourcePath = this.escapeDoubleUnderscores(this.srcFieldPath.trim());
    const wrappedSource = this.wrapTernarySource(escapedSourcePath);

    if (!this.conditions || this.conditions.length === 0) {
      return wrappedSource;
    }

    const conditionsExpr = new ThEachConditionsBuilder('#item', this.conditions);
    return `${wrappedSource}.{? #item=#this, ${conditionsExpr.value}}`;
  }

  private wrapTernarySource(path: string): string {
    const trimmed = path.trim();
    const isTernary = trimmed.includes('?') && trimmed.includes(':');
    if (isTernary && !(trimmed.startsWith('(') && trimmed.endsWith(')'))) {
      return `(${trimmed})`;
    }
    return trimmed;
  }

  private escapeDoubleUnderscores(path: string): string {
    return path.replace(/__/g, '\\_\\_');
  }
}

/**
 * Parsers a raw th:each attribute value.
 */
export class ThEachAttributeParser {
  static getVariableName(element: Element): string {
    const thEach = element.getAttribute(ThEachAttribute.QUALIFIED_NAME);
    if (!thEach) {
      throw new Error(`No ${ThEachAttribute.QUALIFIED_NAME} on element '${element.tagName}'`);
    }

    const nameEndDelimIndex = thEach.indexOf(':');
    return thEach.substring(0, nameEndDelimIndex);
  }
}
